/* import __COLOCATED_TEMPLATE__ from './content.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import { EntityType } from 'embercom/models/data/entity-types';
import { getOwner } from '@ember/application';
import { AiContentState } from 'embercom/lib/ai-content-library/constants';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import {
  BULK_ACTION_PROGRESSION_ID,
  type BulkActionProgressionEvent,
  BulkActionProgressionValues,
  type SyncSourceWrapperResponse,
} from 'embercom/services/knowledge-hub-service';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import ENV from 'embercom/config/environment';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';
import { type ViewId } from 'embercom/components/knowledge-hub/setup-modal';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { type Action } from 'embercom/components/common/hero-banner';
import type { Router } from '@ember/routing';
import KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import type { SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type Folder from 'embercom/models/content-service/folder';
import {
  type InternalArticleSourceType,
  type SyncSourceType,
  SyncStatus,
} from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { hasFeature } from 'embercom/helpers/has-feature';
import type CopilotApi from 'embercom/services/copilot-api';

import { task } from 'ember-concurrency-decorators';
import { timeout, type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { RUN_STATUSES } from 'embercom/lib/external-content/constants';
import {
  CONTENT_IMPORT_RUN_STATUS_TOPIC,
  EVENT_SERVICE_ID,
  SYNC_SUMMARY_POLL_INTERVAL,
} from 'embercom/services/content-import-service';
import { isPresent } from '@ember/utils';

export enum ContentSourceType {
  PublicArticle = 'public_article',
  Website = 'website',
  Snippet = 'snippet',
  PDF = 'pdf',
  ZendeskArticle = 'zendesk_article',
  ConversationExtraction = 'conversation_extraction',
  CustomAnswer = 'custom-answer',
  InternalArticle = 'internal-article',
  ConversationExcerpt = 'conversation_excerpt',
  SalesforceKnowledge = 'salesforce_knowledge',
  FreshdeskKnowledge = 'freshdesk',
  Document360 = 'document360',
  Box = 'box',
  Shopify = 'shopify',
}

export enum ContentSourceStatusType {
  Enabled = 'enabled',
  NotEnabled = 'not_enabled',
  EnabledForAgent = 'enabled_for_agent',
  EnabledForCopilot = 'enabled_for_copilot',
  NotSufficientConversationExcerpts = 'not_sufficient_conversation_excerpts',
  Syncing = 'syncing',
  InProgress = 'in_progress',
  Error = 'error',
  Synced = 'synced',
}

interface Args {
  finIsLive?: boolean;
  openSectionId?: string;
  setOpenSectionId?: (openSectionId: string | undefined) => void;
  showPreview?: boolean;
  togglePreview?: () => void;
  demoType?: string;
}

interface Signature {
  Args: Args;
}

export default class Content extends Component<Signature> {
  entityType = EntityType;
  contentSourceType = ContentSourceType;
  contentSourceStatusType = ContentSourceStatusType;

  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare conversationExtractionService: ConversationExtractionService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare intercomEventService: { trackAnalyticsEvent: Function };
  @service declare router: Router;
  @service declare contentImportService: any;
  @service declare modalService: any;
  @service declare copilotApi: CopilotApi;

  @tracked sourceTypeToImport?: ViewId;
  @tracked showSetupModal = false;
  @tracked previewLocale = 'en';
  @tracked activeVideoId?: string;
  @tracked previewMode: 'agent' | 'copilot' = 'agent';
  @tracked zendeskArticlesUsageBySyncSource: Record<
    string,
    { all: number; copilot: number; agent: number }
  > = {};
  @tracked internalArticlesUsageBySyncSource: Record<string, { all: number; copilot: number }> = {};
  @tracked syncSummaries: Record<number, { imported: number; available: number }> = {};

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.knowledgeHubService.subscribeToSourceSyncEvents();
    this.conversationExtractionService.subscribeToImportProgressEvents();
    this.setBulkActionProgressActions();

    if (args.openSectionId === 'conversation-content') {
      this.showConversationExtractionModal();
      this.args.setOpenSectionId?.(undefined);
    }

    this.initializeContent();

    if (!this.conversationExtractionService.conversationExtractionSettings) {
      this.conversationExtractionService.fetchConversationExtractionSettings();
    }
    this.aiAgentSetupService.load();

    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }

    if (hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)) {
      this.subscribeToImportEvents();
    }
  }

  private async initializeContent() {
    await this.loadUsageSummaryAndSyncSources();
    if (hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)) {
      await this.initializePolling();
    }
  }

  private async initializePolling() {
    await this.fetchSyncSummaries();
    if (this.getExternalContentSyncingSourceIds().length > 0) {
      this.startPolling();
    }
  }

  private subscribeToImportEvents() {
    this.realTimeEventService.subscribeTopics([CONTENT_IMPORT_RUN_STATUS_TOPIC]);
    this.realTimeEventService.on(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  private unsubscribeFromImportEvents() {
    this.realTimeEventService.off(EVENT_SERVICE_ID, this, 'handleImportStatusChange');
  }

  // @ts-ignore - Used as event handler callback in subscribeToImportEvents
  private async handleImportStatusChange(event: any) {
    if (event.event_type === RUN_STATUSES.started) {
      this.startPolling();
    }
  }

  private startPolling() {
    if (!taskFor(this.pollSyncSummaries).isRunning) {
      taskFor(this.pollSyncSummaries).perform();
    }
  }

  private stopPolling() {
    taskFor(this.pollSyncSummaries).cancelAll();
  }

  @task({ restartable: true, maxConcurrency: 1 })
  private *pollSyncSummaries(): TaskGenerator<void> {
    while (true) {
      yield timeout(SYNC_SUMMARY_POLL_INTERVAL);

      if (this.getExternalContentSyncingSourceIds().length === 0) {
        break;
      }
      yield this.fetchSyncSummaries();
    }
  }

  async loadUsageSummaryAndSyncSources() {
    // In case this content is used outside a route that already initialized the usage summary, we need to fetch it
    if (!this.knowledgeHubService.usageSummary) {
      await Promise.all([
        this.knowledgeHubService.fetchKnowledgeUsageSummary(),
        this.knowledgeHubService.fetchFoldersOnce(),
      ]);
    }

    this.loadUsageForZendeskArticleGroups();
    this.loadUsageForInternalArticles();
  }

  private async loadUsageForZendeskArticleGroups() {
    let zendeskFolderIds = this.zendeskSyncSources
      ?.map((source: SyncSourceWrapperResponse) => {
        return source.folder_id;
      })
      .filter((folderId) => isPresent(folderId));

    zendeskFolderIds?.forEach(async (folderId: number) => {
      let folder = this.store.peekRecord('content-service/folder', folderId) as Folder;

      if (!folder) {
        return;
      }

      let baseSearchParams = {
        app_id: this.appService.app.id,
        folder_ids: folder.descendantIds.concat(folderId.toString()),
        object_types: this.knowledgeHubService.getSearchObjectTypes(),
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
        folder_entity_type: EntityType.ContentLibraryFolder,
        sort_by: 'title',
        sort_direction: 'asc' as SortDirection,
      };

      let totalArticles = new KnowledgeHubApi(getOwner(this), baseSearchParams);
      let agentEnabledArticles = new KnowledgeHubApi(getOwner(this), {
        ...baseSearchParams,
        chatbot_state: AiContentState.USED_BY_FIN,
      });
      let copilotEnabledArticles = new KnowledgeHubApi(getOwner(this), {
        ...baseSearchParams,
        copilot_state: AiContentState.USED_BY_FIN,
      });

      await Promise.all([
        totalArticles.loadPage(),
        agentEnabledArticles.loadPage(),
        copilotEnabledArticles.loadPage(),
      ]);

      this.zendeskArticlesUsageBySyncSource = {
        ...this.zendeskArticlesUsageBySyncSource,
        [folderId.toString()]: {
          all: totalArticles.totalCount || 0,
          agent: agentEnabledArticles.totalCount || 0,
          copilot: copilotEnabledArticles.totalCount || 0,
        },
      };
    });
  }

  private async loadUsageForInternalArticles() {
    this.internalArticleSyncSources.forEach(async (source) => {
      let syncSource = this.getInternalArticleSourceWrapper(source);
      if (syncSource && syncSource.folderId) {
        let folder = this.store.peekRecord('content-service/folder', syncSource.folderId) as Folder;

        if (!folder) {
          return;
        }

        let baseSearchParams = {
          app_id: this.appService.app.id,
          folder_ids: folder.descendantIds.concat(syncSource.folderId.toString()),
          object_types: [EntityType.InternalArticle],
          content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
          folder_entity_type: EntityType.ContentLibraryFolder,
          sort_by: 'title',
          sort_direction: 'asc' as SortDirection,
        };

        let totalArticles = new KnowledgeHubApi(getOwner(this), baseSearchParams);

        let copilotEnabledArticles = new KnowledgeHubApi(getOwner(this), {
          ...baseSearchParams,
          copilot_state: AiContentState.USED_BY_FIN,
        });

        await Promise.all([totalArticles.loadPage(), copilotEnabledArticles.loadPage()]);

        this.internalArticlesUsageBySyncSource = {
          ...this.internalArticlesUsageBySyncSource,
          [syncSource.folderId.toString()]: {
            all: totalArticles.totalCount || 0,
            copilot: copilotEnabledArticles.totalCount || 0,
          },
        };
      }
    });
  }

  get numberOfPendingSuggestions() {
    return this.contentImportService.aiContentReviewSummary.pending;
  }

  get zdArticlesTotalCount() {
    return Object.values(this.zendeskArticlesUsageBySyncSource).reduce(
      (acc, curr) => acc + curr.all,
      0,
    );
  }

  get zdArticlesEnabledCount() {
    return Object.values(this.zendeskArticlesUsageBySyncSource).reduce(
      (acc, curr) => acc + curr.all,
      0,
    );
  }

  get zdArticlesCopilotEnabledCount() {
    return Object.values(this.zendeskArticlesUsageBySyncSource).reduce(
      (acc, curr) => acc + curr.all,
      0,
    );
  }

  willDestroy(): void {
    super.willDestroy();
    this.destroyNexusEvents();
    this.knowledgeHubService.unsubscribeToSourceSyncEvents();
    this.conversationExtractionService.unsubscribeFromImportProgressEvents();

    if (hasFeature('team-knowledge-foundations-website-sync-progress', this.appService)) {
      this.stopPolling();
      this.unsubscribeFromImportEvents();
    }
  }

  private setBulkActionProgressActions() {
    this.realTimeEventService.on(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  private destroyNexusEvents() {
    this.realTimeEventService.off(BULK_ACTION_PROGRESSION_ID, this, 'handleBulkActionProgression');
  }

  async handleBulkActionProgression(event: BulkActionProgressionEvent) {
    let currentAdmin = this.appService.app.currentAdmin;
    if (currentAdmin.id !== event.admin_id.toString()) {
      return;
    }

    if (event.status === BulkActionProgressionValues.Complete) {
      if (event.all_entities_unaffected) {
        this.knowledgeHubService.notifyNoEntitiesAffected(
          event.bulk_action,
          this.appService.app.canUseStandalone,
        );
      } else {
        this.sendCompletionNotification(event);
        setTimeout(() => {
          this.knowledgeHubService.fetchKnowledgeUsageSummary();
        }, ENV.APP._500MS);
      }
    }

    this.knowledgeHubService.handleBulkActionFailure(event);
  }

  private async sendCompletionNotification(event: BulkActionProgressionEvent) {
    let action = event.bulk_action;
    let unaffectedEntityTypes = event.unaffected_entity_types;
    let language_code = event.language_code;
    let destinationFolderId = event.destination_folder_id;
    this.knowledgeHubService.notifyBulkActionCompleted(
      action,
      unaffectedEntityTypes,
      language_code,
      destinationFolderId,
      this.appService.app.canUseStandalone,
    );
    if (destinationFolderId) {
      await this.knowledgeHubService.fetchFoldersOnce(true);
    }
  }

  get importedConversationCount() {
    return this.usageSummaryForEntity(EntityType.ConversationExcerpt)?.all || 0;
  }

  get conversationExtractionSettings() {
    return this.conversationExtractionService.conversationExtractionSettings;
  }

  get shouldShowConversationExtractionSection() {
    if (this.appService.app.canSeeSuggestions) {
      return false;
    }
    // If an app is SA then we only want to show this section if canUseExtractionExternalSources  is true, until we release it to all SA apps
    // source https://github.com/intercom/embercom/pull/94249/files#r1946430684
    return (
      !this.appService.app.canUseStandalone || this.appService.app.canUseExtractionExternalSources
    );
  }

  get showEmptyState() {
    return (
      this.totalContentCount === 0 &&
      !this.salesforceKnowledgePresent &&
      !this.conversationExtractionSettings?.enabled &&
      !(this.appService.app.canUseConversationExcerptsOnStandalone && this.excerptsEnabled)
    );
  }

  get excerptsEnabled() {
    return this.copilotApi.copilotSettings?.use_past_conversations ?? true;
  }

  get shouldShowTickets() {
    if (!this.appService.mainStandalonePlatform.supportsConversationSyncing) {
      return false;
    }

    if (!this.appService.mainStandalonePlatform.isConnected) {
      return false;
    }

    return true;
  }

  @action
  getTotalCountForEntity(entityType: EntityType) {
    if (entityType === EntityType.FileSourceContent) {
      return this.totalPdfsCount;
    }

    if (entityType === EntityType.ArticleContent) {
      return this.totalArticlesCount;
    }

    return this.usageSummaryForEntity(entityType)?.all || 0;
  }

  @action
  getChatbotEnabledCountForEntity(entityType: EntityType) {
    if (entityType === EntityType.ArticleContent) {
      return this.enabledArticlesCount;
    }
    if (entityType === EntityType.FileSourceContent) {
      return this.enabledPdfsCount;
    }

    return this.usageSummaryForEntity(entityType)?.agent || 0;
  }

  @action
  getCopilotEnabledCountForEntity(entityType: EntityType) {
    return this.usageSummaryForEntity(entityType)?.copilot || 0;
  }

  get totalArticlesCount() {
    return Math.max(
      (this.usageSummaryForEntity(EntityType.ArticleContent)?.all || 0) - this.zdArticlesTotalCount,
      0,
    );
  }

  get enabledArticlesCount() {
    return Math.max(
      (this.usageSummaryForEntity(EntityType.ArticleContent)?.agent || 0) -
        this.zdArticlesEnabledCount,
      0,
    );
  }

  get copilotEnabledArticlesCount() {
    return Math.max(
      (this.usageSummaryForEntity(EntityType.ArticleContent)?.copilot || 0) -
        this.zdArticlesCopilotEnabledCount,
      0,
    );
  }

  get showPdfsSourceRow() {
    return (
      (this.usageSummaryForEntity(EntityType.FileSourceContent)?.all || 0) >
      this.totalSyncedDocumentCount
    );
  }

  get enabledChatbotCountForSalesforce() {
    return this.salesforceSyncSources.reduce((acc, src) => acc + (src.usage?.agent || 0), 0);
  }

  private get enabledInternalArticlesCount() {
    // TODO: update this logic once we have the usage data for internal articles from external sources.
    return this.usageSummaryForEntity(EntityType.InternalArticle)?.copilot || 0;
  }

  private get totalInternalArticlesCount() {
    return this.usageSummaryForEntity(EntityType.InternalArticle)?.all || 0;
  }

  private get totalSnippetsCount() {
    return this.usageSummaryForEntity(EntityType.ContentSnippet)?.all || 0;
  }

  private get enabledSnippetsCount() {
    return this.usageSummaryForEntity(EntityType.ContentSnippet)?.agent || 0;
  }

  private get totalSyncedDocumentCount() {
    return (
      this.usageSummaryForEntity(EntityType.FileSourceContent)?.sync_sources.reduce(
        (acc, src) => acc + (src.usage?.all || 0),
        0,
      ) || 0
    );
  }

  private get enabledSyncedDocumentCount() {
    return (
      this.usageSummaryForEntity(EntityType.FileSourceContent)?.sync_sources.reduce(
        (acc, src) => acc + (src.usage?.agent || 0),
        0,
      ) || 0
    );
  }

  private get totalPdfsCount() {
    return (
      (this.usageSummaryForEntity(EntityType.FileSourceContent)?.all || 0) -
      this.totalSyncedDocumentCount
    );
  }

  private get enabledPdfsCount() {
    return (
      (this.usageSummaryForEntity(EntityType.FileSourceContent)?.agent || 0) -
      this.enabledSyncedDocumentCount
    );
  }

  private get salesforceSyncSources() {
    return (
      this.usageSummaryForEntity(this.getSalesforceEntityType())?.sync_sources.filter(
        (src) => src.source_type === 'salesforce_knowledge',
      ) ?? []
    );
  }

  get freshdeskSyncSources() {
    return (
      this.usageSummaryForEntity(EntityType.FileSourceContent)?.sync_sources.filter(
        (src) => src.source_type === 'freshdesk',
      ) ?? []
    );
  }

  get salesforceCount() {
    return this.salesforceSyncSources.reduce((acc, src) => acc + (src.usage?.all || 0), 0);
  }

  private get salesforceKnowledgePresent() {
    return (
      (this.usageSummaryForEntity(this.getSalesforceEntityType())?.sync_sources.length || 0) > 0
    );
  }

  private get enabledExternalContentCount() {
    return this.usageSummaryForEntity(EntityType.ExternalContent)?.agent || 0;
  }

  private get totalExternalContentCount() {
    return this.usageSummaryForEntity(EntityType.ExternalContent)?.sync_sources.length || 0;
  }

  private usageSummaryForEntity(entityType: EntityType) {
    return this.knowledgeHubService.usageSummary?.[entityType];
  }

  get numberOfPendingItemsToReview() {
    return this.contentImportService.aiContentReviewSummary.pending;
  }

  @action
  setPreviewMode(mode: 'agent' | 'copilot') {
    this.previewMode = mode;
  }

  @action
  getContentStatus(entityType: EntityType) {
    let agentEnabledCount = this.getChatbotEnabledCountForEntity(entityType);
    let copilotEnabledCount = this.getCopilotEnabledCountForEntity(entityType);

    if (this.appService.app.canUseCopilotOnStandalone) {
      if (agentEnabledCount > 0 && copilotEnabledCount > 0) {
        return ContentSourceStatusType.Enabled;
      } else if (agentEnabledCount > 0) {
        return ContentSourceStatusType.EnabledForAgent;
      } else if (copilotEnabledCount > 0) {
        return ContentSourceStatusType.EnabledForCopilot;
      } else {
        return ContentSourceStatusType.NotEnabled;
      }
    } else {
      return agentEnabledCount > 0
        ? ContentSourceStatusType.Enabled
        : ContentSourceStatusType.NotEnabled;
    }
  }

  @action
  getIsEntityEligibleForCopilot(entityType: EntityType) {
    return this.usageSummaryForEntity(entityType)?.is_eligible_for_copilot || false;
  }

  @action
  getIsEntityEligibleForChatbot(entityType: EntityType) {
    return this.usageSummaryForEntity(entityType)?.is_eligible_for_chatbot || false;
  }

  get totalEnabledContentCount() {
    return (
      this.enabledArticlesCount +
      this.enabledSnippetsCount +
      this.enabledPdfsCount +
      this.enabledExternalContentCount +
      this.enabledSyncedDocumentCount
    );
  }

  get totalContentCount() {
    return (
      this.totalArticlesCount +
      this.totalSnippetsCount +
      this.totalPdfsCount +
      this.totalExternalContentCount +
      this.totalSyncedDocumentCount
    );
  }

  get disabledArticlesCriteria() {
    return {
      disableBulkEnable: this.enabledArticlesCount === this.totalArticlesCount,
      disableBulkEnableCopilot: this.copilotEnabledArticlesCount === this.totalArticlesCount,
    };
  }

  get disabledInternalArticlesCriteria() {
    let disableBulkEnable = !this.appService.app.canEnableFinForInternalArticles;

    // If feature flag is enabled, check if all internal articles are already enabled for AI agent
    if (this.appService.app.canEnableFinForInternalArticles) {
      let enabledInternalArticlesForChatbot =
        this.usageSummaryForEntity(EntityType.InternalArticle)?.agent || 0;
      disableBulkEnable = enabledInternalArticlesForChatbot === this.totalInternalArticlesCount;
    }

    return {
      disableBulkEnable,
      disableBulkEnableCopilot:
        this.enabledInternalArticlesCount === this.totalInternalArticlesCount,
    };
  }

  @action isExternalContentSyncDisabled(syncSource: SyncSourceWrapper): boolean {
    if (
      syncSource.errorType === 'non_retryable_error' ||
      syncSource.errorType === 'no_content' ||
      syncSource.errorType === 'unauthorized'
    ) {
      return true;
    } else {
      return false;
    }
  }

  get disabledSnippetsCriteria() {
    return {
      disableBulkEnable: this.enabledSnippetsCount === this.totalSnippetsCount,
      disableBulkEnableCopilot:
        (this.usageSummaryForEntity(EntityType.ContentSnippet)?.copilot || 0) ===
        this.totalSnippetsCount,
    };
  }

  get disabledPdfsCriteria() {
    return {
      disableBulkEnable: this.enabledPdfsCount === this.totalPdfsCount,
      disableBulkEnableCopilot:
        (this.usageSummaryForEntity(EntityType.FileSourceContent)?.copilot || 0) ===
        this.totalPdfsCount,
    };
  }

  get externalContentSyncSources(): SyncSourceWrapperResponse[] {
    return this.usageSummaryForEntity(EntityType.ExternalContent)?.sync_sources ?? [];
  }

  get internalArticleSyncSources(): SyncSourceWrapperResponse[] {
    return this.usageSummaryForEntity(EntityType.InternalArticle)?.sync_sources ?? [];
  }

  get fileSourceSyncSources(): SyncSourceWrapperResponse[] {
    return this.usageSummaryForEntity(EntityType.FileSourceContent)?.sync_sources ?? [];
  }

  get document360SyncSources(): SyncSourceWrapperResponse[] {
    return this.fileSourceSyncSources.filter((source) => source.source_type === 'document360');
  }

  get boxSyncSources(): SyncSourceWrapperResponse[] {
    return this.fileSourceSyncSources.filter((source) => source.source_type === 'box');
  }

  @action getExternalContentSourceWrapper(
    externalContentSource: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${externalContentSource.source_type}-${externalContentSource.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getInternalArticleSourceWrapper(
    internalArticleSource: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${internalArticleSource.source_type}-${internalArticleSource.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getDocument360SyncSourceWrapper(
    document360Source: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${document360Source.source_type}-${document360Source.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getBoxSyncSourceWrapper(
    boxSource: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${boxSource.source_type}-${boxSource.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getInternalArticleSourceUsage(syncSource: SyncSourceWrapper) {
    if (syncSource.folderId) {
      return this.internalArticlesUsageBySyncSource[syncSource.folderId.toString()];
    }

    return;
  }

  @action getInternalArticleSyncSourceTranslation(sourceType: SyncSourceType) {
    return this.intl.t(
      `fin-ai-agent.knowledge.content-sources.with-sync-sources.${sourceType as InternalArticleSourceType}`,
    );
  }

  @action getInternalArticleStatus(usage: { all: number; copilot: number }) {
    return usage.copilot === 0
      ? ContentSourceStatusType.NotEnabled
      : ContentSourceStatusType.EnabledForCopilot;
  }

  @action goToContent(entityType: EntityType, syncSource?: SyncSourceWrapper) {
    if (
      [
        EntityType.ExternalContent,
        EntityType.InternalArticle,
        EntityType.FileSourceContent,
        EntityType.ArticleContent,
      ].includes(entityType) &&
      syncSource
    ) {
      this.knowledgeHubService.goToSyncSourceFolder(
        syncSource,
        {},
        false,
        ![
          EntityType.ExternalContent,
          EntityType.FileSourceContent,
          EntityType.ArticleContent,
        ].includes(entityType),
      );
    } else if (entityType === EntityType.FileSourceContent) {
      // check to split the documents between the file and other sources
      this.knowledgeHubService.goToContent(entityType, { sourceType: EntityType.FileSource });
    } else {
      this.knowledgeHubService.goToContent(entityType, {});
    }
  }

  get zendeskSyncSources(): SyncSourceWrapperResponse[] | undefined {
    if (!this.knowledgeHubService.usageSummary) {
      return [];
    }

    return this.usageSummaryForEntity(EntityType.ArticleContent)?.sync_sources.filter(
      ({ source_type }: { source_type: string }) => source_type === 'zendesk',
    );
  }

  @action getZendeskSyncSourceWrapper(source: SyncSourceWrapperResponse): SyncSourceWrapper {
    let id = `${source.source_type}-${source.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getZendeskArticleSourceUsage(syncSource: SyncSourceWrapper) {
    if (!syncSource.folderId) {
      return;
    }

    return this.zendeskArticlesUsageBySyncSource[syncSource.folderId.toString()];
  }

  get salesforceSyncSource(): SyncSourceWrapperResponse | undefined {
    if (!this.knowledgeHubService.usageSummary) {
      return;
    }

    return this.fileSourceSyncSources.find(
      (source: SyncSourceWrapperResponse) => source.source_type === 'salesforce_knowledge',
    );
  }

  get salesforceSyncSourceWrapper(): SyncSourceWrapper | undefined {
    if (!this.salesforceSyncSource) {
      return undefined;
    }
    let id = `${this.salesforceSyncSource.source_type}-${this.salesforceSyncSource.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  get freshdeskSyncSource(): SyncSourceWrapperResponse | undefined {
    if (!this.knowledgeHubService.usageSummary) {
      return;
    }

    return this.fileSourceSyncSources.find(
      (source: SyncSourceWrapperResponse) => source.source_type === 'freshdesk',
    );
  }

  get freshdeskSyncSourceWrapper(): SyncSourceWrapper | undefined {
    if (!this.freshdeskSyncSource) {
      return undefined;
    }
    let id = `${this.freshdeskSyncSource.source_type}-${this.freshdeskSyncSource.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getFreshdeskSyncSourceWrapper(
    source: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${source.source_type}-${source.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action getSalesforceSyncSourceWrapper(
    source: SyncSourceWrapperResponse,
  ): SyncSourceWrapper | undefined {
    let id = `${source.source_type}-${source.source_id}`;
    return this.store.peekRecord('knowledge-hub/sync-source-wrapper', id);
  }

  @action
  async showModal(syncSourceType: string, entityType: EntityType) {
    try {
      await this.knowledgeHubService.ensurePermissions(entityType);
    } catch (error) {
      return;
    }

    this.sourceTypeToImport = syncSourceType as ViewId;
    this.showSetupModal = true;
  }

  @action
  closeSetupModal() {
    this.showSetupModal = false;
    this.knowledgeHubService.onSummaryChange();
    if (this.sourceTypeToImport === 'conversation-extraction') {
      this.conversationExtractionService.maybeRollbackConversationExtractionSettings();
    }
    this.sourceTypeToImport = undefined;
  }

  @action
  async showConversationExtractionModal() {
    // awaiting here to check permissions
    await this.showModal('conversation-extraction', EntityType.ContentSnippet);
  }

  @action
  goToConversationFolder() {
    if (this.appService.app.canUseStandalone) {
      this.router.transitionTo('apps.app.standalone.knowledge.suggestions');
    } else {
      this.router.transitionTo('apps.app.automation.fin-ai-agent.content-suggestions');
    }
  }

  get hasContentReadyForFin() {
    return this.aiAgentSetupService.hasContentReadyForFin;
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  get newHeroBannerTitle(): string {
    return this.appService.app.canUseStandalone
      ? 'knowledge-hub.new-banner.standalone-title'
      : `knowledge-hub.new-banner.${this.totalEnabledContentCount > 0 ? 'has-content-title' : 'no-content-title'}`;
  }

  get newHeroBannerDescription(): string {
    return this.appService.app.canUseStandalone
      ? 'knowledge-hub.new-banner.standalone-description'
      : `knowledge-hub.new-banner.${this.totalEnabledContentCount > 0 ? 'has-content-description' : 'no-content-description'}`;
  }

  @action
  recordAnalyticsEvent(analyticsId: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: analyticsId,
      place: 'knowledge-hub-overview-header',
    });
  }

  get newHeroBannerActions(): Action[] {
    if (this.appService.app.canUseStandalone) {
      return this.getStandaloneHeroBannerActions();
    }
    if (this.totalEnabledContentCount > 0) {
      return [
        {
          type: 'link',
          label: 'knowledge-hub.new-banner.actions.article-optimizing-content-for-fin',
          icon: 'article',
          analyticsId: 'optimizing-content-for-fin-article',
          onClick: () => {
            this.recordAnalyticsEvent('optimizing-content-for-fin-article');
            window.Intercom('showArticle', 7860255); // https://www.intercom.com/help/en/articles/7860255-optimizing-content-for-fin
          },
        },
      ];
    } else {
      return [
        {
          type: 'link',
          label: 'knowledge-hub.new-banner.actions.fin-ai-agent-features',
          icon: 'article',
          analyticsId: 'fin-ai-agent-features',
          onClick: () => {
            this.recordAnalyticsEvent('fin-ai-agent-features');
            window.Intercom('showArticle', 7120684); // https://www.intercom.com/help/en/articles/7120684-fin-2-intercom-s-latest-generation-of-ai-agent
          },
        },
        {
          type: 'link',
          label: 'knowledge-hub.new-banner.actions.adding-content-for-fin',
          icon: 'article',
          analyticsId: 'adding-content-for-fin',
          onClick: () => {
            this.recordAnalyticsEvent('adding-content-for-fin');
            window.Intercom('showArticle', 7837514); // https://www.intercom.com/help/en/articles/7837514-add-your-support-content-for-fin-ai-agent
          },
        },
      ];
    }
  }

  private getStandaloneHeroBannerActions(): Action[] {
    let standaloneActions: Action = {
      type: 'link',
      label: 'knowledge-hub.new-banner.actions.sync-manage-websites',
      icon: 'article',
      analyticsId: 'sync-manage-websites',
      onClick: () => {
        this.recordAnalyticsEvent('sync-manage-websites');
        window.Intercom('showArticle', 10617480); // https://fin.ai/help/en/articles/10617480-sync-and-manage-websites
      },
    };

    let platformActions: Action[] = [
      ...(this.appService.mainStandalonePlatform.componentData['knowledge-content']
        ?.heroBannerActions ?? []),
    ];

    platformActions.forEach((action) => {
      action.onClick = action.onClick?.bind(this);
    });

    platformActions.push(standaloneActions);

    return platformActions;
  }

  get bannerDismissalKey() {
    if (this.appService.app.canUseStandalone) {
      return 'standalone-knowledge-hub-overview-header';
    }
    if (this.totalEnabledContentCount > 0) {
      return 'has-content-enabled-knowledge-hub-overview-header';
    }
    return 'no-content-enabled-knowledge-hub-overview-header';
  }

  @action
  setActiveVideo(videoId: string) {
    this.activeVideoId = videoId;
  }

  @action
  trackShowAllContentClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'show_all_content_button',
      place: 'fin_knowledge_overview',
      section: 'content_sources',
    });
  }

  @action
  trackSuggestionsButtonClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'suggestions_page_button',
      place: 'fin_knowledge_overview',
      section: 'content_sources',
    });
  }

  private getExternalContentSyncingSourceIds() {
    return this.externalContentSyncSources
      .filter((source) => {
        if (source.status !== SyncStatus.SYNCING || !source.source_id || !source.folder_id) {
          return false;
        }
        let folder = this.store.peekRecord('content-service/folder', source.folder_id) as Folder;
        return folder?.firstTimeSyncing || false;
      })
      .map((source) => Number(source.source_id));
  }

  private async fetchSyncSummaries(): Promise<void> {
    let syncingSourcesIds = this.getExternalContentSyncingSourceIds();

    if (syncingSourcesIds.length) {
      try {
        let response = await this.knowledgeHubService.getSyncSummary(syncingSourcesIds.join(','));
        this.syncSummaries = response.reduce(
          (acc: Record<number, { imported: number; available: number }>, summary: any) => {
            if (typeof summary.source_id === 'number') {
              acc[summary.source_id] = { imported: summary.imported, available: summary.available };
            }
            return acc;
          },
          {},
        );
      } catch (e) {
        this.syncSummaries = {};
      }
    } else {
      this.syncSummaries = {};
    }
  }

  @action
  showContentCount(source: SyncSourceWrapperResponse): boolean {
    if (!source?.folder_id) {
      return false;
    }
    let folder = this.store.peekRecord('content-service/folder', source.folder_id) as Folder;
    return !(source.status === SyncStatus.SYNCING && folder && folder.firstTimeSyncing);
  }

  @action
  getStatusText(source: SyncSourceWrapperResponse): string | undefined {
    if (source.source_id && source.status === SyncStatus.SYNCING) {
      let syncSummary = this.syncSummaries[source.source_id];
      if (syncSummary?.available && syncSummary.available > 0) {
        return this.intl.t('knowledge-hub.sync-progress-count', {
          imported: syncSummary.imported,
          available: syncSummary.available,
        });
      }
    }
    return undefined;
  }

  @action
  getStatusTooltip(source: SyncSourceWrapperResponse): string | undefined {
    if (source.source_id && source.status === SyncStatus.SYNCING) {
      let syncSummary = this.syncSummaries[source.source_id];
      if (syncSummary?.available && syncSummary.available > 0) {
        return this.intl.t('knowledge-hub.sync-progress-count-tooltip');
      }
    }
    return undefined;
  }

  @action
  getSalesforceEntityType(): EntityType {
    return this.appService.app.canUseSalesforceArticleSynchroniser
      ? EntityType.ArticleContent
      : EntityType.FileSourceContent;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Knowledge::Content': typeof Content;
  }
}
